<template>
  <v-skeleton-loader
    transition="transition"
    type="article"
    class="mb-5"
    :loading="loading"
  >
    <v-card
      class="mb-5 py-4 px-2 subtitle-1 grey--text text--darken-3"
      data-cy="card_resumo_da_compra"
    >
      <div v-if="!loading">
        <v-card-title
          class="primary--text justify-center font-weight-bold text-uppercase subtitle-2 pa-0 mb-2"
          data-cy="resumo_da_compra"
        >
          Resumo da {{ tituloCard }}
        </v-card-title>
        <v-alert
          v-if="alertaAssinaturaCancelada && !vendaParcela"
          class="body-2 mb-2 ml-3 mr-3"
          color="warning"
          dark
        >
          O cancelamento de assinatura
          <strong class="text-decoration-underline">
            não implica no reembolso automático
          </strong>
          de valores já pagos, apenas interrompe cobranças futuras.
          Para solicitar reembolso acesse o menu “Ainda preciso de ajuda”.
        </v-alert>
        <v-alert
          v-if="dados.order_bump_assinatura_show_message"
          class="body-2 my-4 ml-3 mr-3"
          color="info"
          dark
          dense
        >
          <b>Atenção:</b> O valor de adesão da sua assinatura foi cobrado junto ao produto principal em que a mesma foi adquirida. Para visualizar essa e outras informações, consulte o produto <b>{{dados.order_bump_assinatura_parent_product}}</b> da transação <b>{{dados.order_bump_assinatura_parent_order}}</b>.
        </v-alert>
        <v-row
          no-gutters
          class="mb-1 pt-1 px-3"
          :class="corStatus(retornaStatusFlag, true)"
          data-cy="status_compra"
        >
          <v-alert
            v-if="exibirAlertaNomeFatura"
            class="mt-4"
            color="info"
            dark
            dense
            data-cy="informacao_nome_na_fatura"
          >
            ATENÇÃO: em sua fatura aparecerá a descrição
            <b>{{ dados.credit_cards[0].invoice_name }}</b>
          </v-alert>
          <v-icon
            class="mr-1"
            :color="corStatus(retornaStatusFlag)"
          >
            {{ iconeStatus(retornaStatusFlag) }}
          </v-icon>
          {{ retornaStatus }}
        </v-row>

        <div v-if="dados.is_membership || vendaParcela">
          <v-row
            no-gutters
            class="px-3"
            data-cy="data_da_aquisicao"
          >
            Adquirida em {{ dados.membership_created_at | dataFormatada }}
          </v-row>
          <v-row
            no-gutters
            class="px-3"
            data-cy="periodicidade"
          >
            Periodicidade:
            <span class="font-weight-bold mx-1">
              {{ dados.billing_frequency }}
            </span>
          </v-row>
          <v-row
            v-show="dados.next_invoice_date"
            no-gutters
            class="px-3"
            data-cy="ultima_cobranca"
          >
            Fatura atual: <span v-if="!dados.order_bump_assinatura_show_message" class="font-weight-bold ms-1">
              {{ dados.last_invoice_amount | moeda }}
            </span> <span class="grey--text text--darken-1 ms-1">
              ({{ dados.last_invoice_date | dataFormatada('2-digit', 'numeric') }})
            </span>
          </v-row>
          <v-row
            v-if="dados.membership_status !== 'Cancelada' && !vendaParcela"
            no-gutters
            class="px-3"
            data-cy="proxima_cobranca"
          >
            Próxima fatura: <span class="font-weight-bold mx-1">
              {{ dados.next_invoice_amount | moeda }}
            </span> <span class="grey--text text--darken-1">
              ({{ dados.next_invoice_date | dataFormatada('2-digit', 'numeric') }})
            </span>
          </v-row>
          <v-row
            no-gutters
            class="px-3"
          >
            <v-btn
              v-if="dados.boleto_url"
              :href="dados.boleto_url"
              class="mt-2 px-1"
              color="success"
              data-cy="btn_gerar_boleto"
              small
              target="_blank"
              text
            >
              <v-icon left>
                fas fa-barcode
              </v-icon>
              Gerar boleto
            </v-btn>

            <v-btn
              outlined
              block
              color="primary"
              class="my-4"
              data-cy="btn_gerenciar_assinatura"
              :to="linkBtn"
            >
              <template v-if="vendaParcela">
                Trocar cartão
              </template>
              <template v-else>
                Gerenciar assinatura
              </template>
            </v-btn>
          </v-row>
        </div>
        <div v-else>
          <v-row
            no-gutters
            class="my-1 px-3"
            data-cy="dia_da_compra"
          >
            Compra em {{ dados.created_at | dataFormatada }}
          </v-row>
          <v-row
            v-if="dados.paid_at"
            no-gutters
            class="grey--text text--darken-1 body-2 px-3"
            data-cy="dia_do_pagamento"
          >
            Pagamento em {{ dados.paid_at | dataFormatada }}
          </v-row>
        </div>
        <v-divider class="mt-2" />
        <v-container
          v-for="(produto, index) in dadosProdutos"
          :key="index"
          class="pa-0"
        >
          <p
            v-if="index > 0"
            class="ma-0 text-center"
          >
            +
          </p>
          <v-row
            no-gutters
            class="primary--text font-weight-bold px-3"
            data-cy="nome_produto"
            style="word-break: break-word;"
            :class="{ 'mt-4': index === 0 }"
          >
            {{ produto.name }} - {{ produto.plan_name }}
          </v-row>
          <v-row
            no-gutters
            class="px-3"
          >
            <v-col data-cy="total_unidades">
              Valor total ({{ produto.quantity }} uni)
            </v-col>
            <v-col class="font-weight-bold text-right" data-cy="valor_total">
              <template v-if="vendaParcela">
                {{ totalVendaParcelada | moeda }}
              </template>
              <template v-else-if="dados.order_bump_assinatura_show_message">
                -
              </template>
              <template v-else>
                {{ produto.total | moeda }}
              </template>
            </v-col>
          </v-row>
        </v-container>
        <template v-if="!vendaParcela">
          <v-row
            v-for="cartao in dadosCartao"
            :key="cartao.id"
            no-gutters
            class="mt-4 px-3"
          >
            <v-col>
              <p
                class="primary--text font-weight-bold mb-0"
                data-cy="numero_cartao"
              >
                <v-icon
                  color="primary"
                  class="mr-1"
                  small
                  data-cy="icone_cartao_credito"
                >
                  far fa-credit-card
                </v-icon>
                ****{{ cartao.card_number }}
              </p>
              <p class="mb-0 grey--text text--darken-1">
                em {{ cartao.instalment_quantity }}x de
              </p>
            </v-col>
            <v-col class="text-right" data-cy="valor_total">
              <p class="mb-0">
                {{ cartao.total | moeda }}
              </p>
              <p
                class="mb-0 grey--text text--darken-1"
                style="position: relative;"
              >
                {{ cartao.instalment_value | moeda }}
                <span
                  v-if="dados.is_credit_card"
                  style="position: absolute;right: -6px;top: -2px"
                >
                  *
                </span>
              </p>
            </v-col>
          </v-row>
        </template>
        <v-row
          v-if="dados.is_boleto || dados.is_monetizze_balance || dados.is_boleto_in_installments"
          no-gutters
          class="mt-4 px-3"
        >
          <v-col
            v-if="dados.is_boleto || dados.is_boleto_in_installments"
            class="primary--text font-weight-bold mb-0"
          >
            <v-icon
              color="primary"
              small
              class="mb-1 mr-1"
              data-cy="icone_boleto"
            >
              fas fa-barcode
            </v-icon>
            Boleto
          </v-col>
          <v-col
            v-if="dados.is_monetizze_balance"
            class="primary--text font-weight-bold mb-0"
          >
            <v-icon
              tag="img"
              :src="require('@/assets/img/saldo-monetizze.png')"
              height="18"
              class="mb-1 mr-1"
              data-cy="icone_saldo_monetizze"
            />
            Saldo Monetizze
          </v-col>
          <v-col v-if="!dados.order_bump_assinatura_show_message" class="text-right" data-cy="valor_total">
            {{ dados.total | moeda }}
          </v-col>
          <v-col v-else class="text-right">
            -
          </v-col>
        </v-row>
        <v-row
          v-else-if="dados.is_pix || dados.is_paypal"
          no-gutters
          class="mt-4 px-3"
        >
          <v-col cols="7" class="mb-0">
            <v-img
              v-if="dados.is_pix"
              :src="require(`@/assets/img/logo-pix.png`)"
              alt="Logo Pix"
              class="mt-1"
              data-cy="icone_pix"
              height="17"
              width="48"
            />
            <v-img
              v-else-if="dados.is_paypal"
              :src="require(`@/assets/img/logo-paypal.png`)"
              alt="Logo Paypal"
              class="mt-1"
              data-cy="icone_paypal"
              height="20"
              width="64"
            />
          </v-col>
          <v-col v-if="!dados.order_bump_assinatura_show_message" class="text-right" data-cy="valor_total">
            {{ dados.total | moeda }}
          </v-col>
          <v-col v-else class="text-right">
            -
          </v-col>
        </v-row>
        <v-divider class="mt-2 mb-4" />
        <span
          class="grey--text text--darken-2 px-3"
          data-cy="numero_transacao"
        >
          Transação {{ dados.id }}
        </span>
        <v-row
          v-if="dados.is_credit_card"
          class="px-3"
        >
          <p
            class="grey--text text--darken-2 px-3 caption"
            data-cy="msg_juros"
          >
            * Valores parcelados podem incluir taxas e encargos.
          </p>
        </v-row>
      </div>
    </v-card>
  </v-skeleton-loader>
</template>

<script>
export default {
  name: 'CardResumoCompra',

  props: {
    data: {
      type: Object,
      required: true,
    },
    loading: {
      type: Boolean,
      required: true,
    },
  },

  computed: {
    alertaAssinaturaCancelada() {
      const dataHoje = new Date();
      const dataFinal = new Date(this.dados.membership_modified_at);
      dataFinal.setDate(dataFinal.getDate() + 20);
      return this.dados.membership_status === 'Cancelada' && dataHoje < dataFinal;
    },
    dados() {
      return this.data;
    },
    dadosCartao() {
      return this.dados.is_credit_card || this.dados.is_debit_card ? this.data.credit_cards : [];
    },
    dadosProdutos() {
      return this.data.products;
    },
    exibirAlertaNomeFatura() {
      return this.dados.credit_cards.length && this.dados.credit_cards[0].invoice_name;
    },
    tituloCard() {
      if (this.dados.is_membership) return 'Assinatura';
      if (this.vendaParcela) return 'Compra Parcelada';
      return 'Compra';
    },
    linkBtn() {
      if (this.vendaParcela) return '/meu-perfil/cartoes-credito';
      return `/minhas-compras/gerenciar-assinatura/${this.dados.id}`;
    },
    vendaParcela() {
      return this.dados.is_instalment_recovery;
    },
    totalVendaParcelada() {
      const { dados } = this;
      const valor = dados.next_invoice_amount || dados.last_invoice_amount - dados.freight_cost;

      return valor * dados.max_recurrence_instalments + dados.freight_cost;
    },
    retornaStatus() {
      if (this.vendaParcela) return this.dados.status;
      return this.dados.membership_status || this.dados.status;
    },
    retornaStatusFlag() {
      if (this.vendaParcela) return this.dados.status_flag;
      return this.dados.membership_status_flag || this.dados.status_flag;
    },
  },

  methods: {
    corStatus(flag, possuiTexto = false) {
      const texto = possuiTexto ? '--text' : '';

      return flag + texto;
    },

    iconeStatus(status) {
      if (status === 'warning') return 'error';
      if (status === 'success') return 'check_circle';
      return 'cancel';
    },
  },
};
</script>
